import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Send, MapPin } from "lucide-react";
import { MaskedLine, Reveal, Overline } from "@/components/Reveal";
import { useCaptcha, HumanCheck } from "@/components/HumanCheck";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ["Suggestion", "General Feedback", "Restock Request", "Machine Issue", "Cleanliness", "Other"];

const Feedback = () => {
  const [form, setForm] = useState({ location_text: "", name: "", email: "", category: "", message: "", company: "" });
  const [sending, setSending] = useState(false);
  const [searchParams] = useSearchParams();
  const qrLocationId = searchParams.get("location");
  const [qrLocationName, setQrLocationName] = useState(null);
  const [loadingQr, setLoadingQr] = useState(!!qrLocationId);
  const quickMode = !!qrLocationName;
  const captcha = useCaptcha();

  useEffect(() => {
    if (!qrLocationId) return;
    axios.get(`${API}/location-name/${qrLocationId}`)
      .then((res) => {
        setQrLocationName(res.data.name);
        setForm((f) => ({ ...f, category: f.category || "General Feedback" }));
      })
      .catch(() => toast.error("This QR code's location is no longer active — please type your location instead."))
      .finally(() => setLoadingQr(false));
  }, [qrLocationId]);

  useEffect(() => { captcha.checkRequired(); }, [captcha.checkRequired]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!quickMode && !form.location_text.trim()) {
      toast.error("Please tell us your location first.");
      return;
    }
    if (!form.category) {
      toast.error("Please pick a category.");
      return;
    }
    setSending(true);
    try {
      const payload = { ...form, ...captcha.fields };
      if (quickMode) {
        payload.location_id = qrLocationId;
        payload.location_text = qrLocationName;
      }
      if (!payload.email) delete payload.email;
      await axios.post(`${API}/feedback`, payload);
      toast.success(form.email
        ? "Feedback sent — a confirmation is on its way to your inbox."
        : "Feedback sent — thank you!");
      setForm((f) => ({ location_text: f.location_text, name: "", email: "", category: quickMode ? "General Feedback" : "", message: "", company: "" }));
      captcha.load().catch(() => {});
    } catch (err) {
      const rejected = await captcha.handleReject(err);
      if (rejected === "required") {
        toast.info("Quick human check — answer the question to send.");
      } else if (rejected === "wrong") {
        toast.error("Wrong answer — try the new question.");
      } else {
        toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Something went wrong. Please try again.");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="feedback-page">
      <section className="relative overflow-hidden pt-36 pb-24 lg:pt-48 lg:pb-36">
        <div aria-hidden="true" className="absolute -top-32 right-1/4 h-[26rem] w-[26rem] rounded-full bg-vortex-cyan/10 blur-[130px]" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <MaskedLine delay={0.1}>
            <Overline>Current Customers — Feedback & Suggestions</Overline>
          </MaskedLine>
          <h1 className="mt-8 font-heading font-black tracking-tighter leading-[0.95] text-5xl sm:text-7xl" data-testid="feedback-heading">
            <MaskedLine delay={0.25}>TELL US HOW</MaskedLine>
            <MaskedLine delay={0.4}>
              WE'RE <span className="text-vortex-cyan">DOING.</span>
            </MaskedLine>
          </h1>
          <Reveal delay={0.55}>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-slate-400">
              Tell us your location and anything else — product suggestions, restock
              requests, or issues. Scanned a QR code on a machine? Your location is
              already filled in. Every message lands directly with our team.
            </p>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 gap-12 lg:grid-cols-12">
            <Reveal delay={0.6} className="lg:col-span-4">
              <div className="border border-white/10 bg-vortex-surface/60 p-8 backdrop-blur-xl" data-testid="feedback-location-panel">
                <MapPin size={22} className="text-vortex-cyan" />
                <h2 className="mt-4 font-heading text-xl font-bold text-white">Step 1 — Your location</h2>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">
                  So the right route operator sees your message immediately.
                </p>
                <div className="mt-6 space-y-2">
                  <Label className="font-mono text-[10px] tracking-[0.25em] text-slate-400 uppercase">Location</Label>
                  {loadingQr ? (
                    <p className="border border-white/10 bg-vortex-ink/60 px-4 py-3 text-sm text-slate-500" data-testid="feedback-location-loading">
                      Verifying machine QR code...
                    </p>
                  ) : quickMode ? (
                    <div className="flex items-center gap-3 border border-vortex-cyan/40 bg-vortex-cyan/10 px-4 py-3" data-testid="feedback-location-locked">
                      <MapPin size={15} className="shrink-0 text-vortex-cyan" />
                      <span className="text-sm font-medium text-slate-100">{qrLocationName}</span>
                      <span className="ml-auto font-mono text-[9px] tracking-[0.2em] text-vortex-cyan uppercase">Auto-filled</span>
                    </div>
                  ) : (
                    <Input id="fb-location" data-testid="feedback-location-input" required value={form.location_text}
                      onChange={set("location_text")}
                      className="border-white/10 bg-vortex-ink/60 text-slate-100 focus-visible:ring-vortex-cyan"
                      placeholder="Type your location, e.g. Harbor Tech Park — Floor 3" />
                  )}
                  {!quickMode && !loadingQr && (
                    <p className="text-xs text-slate-500">
                      Scanned a QR code on one of our machines? Your location fills in automatically.
                    </p>
                  )}
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.7} className="lg:col-span-8">
              <form
                onSubmit={submit}
                className="border border-white/10 bg-vortex-surface/60 p-8 backdrop-blur-xl glow-cyan lg:p-12"
                data-testid="feedback-form"
              >
                <p className="font-mono text-[10px] tracking-[0.25em] text-slate-500 uppercase">Step 2 — Your message</p>
                {quickMode && (
                  <p className="mt-3 inline-block rounded-full border border-vortex-cyan/40 px-4 py-1.5 font-mono text-[10px] tracking-[0.2em] text-vortex-cyan uppercase" data-testid="quick-mode-badge">
                    QR quick mode — no email needed
                  </p>
                )}
                <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="fb-name" className="font-mono text-[10px] tracking-[0.25em] text-slate-400 uppercase">
                      Name{quickMode ? " (optional)" : ""}
                    </Label>
                    <Input id="fb-name" data-testid="feedback-name-input" required={!quickMode} value={form.name} onChange={set("name")}
                      className="border-white/10 bg-vortex-ink/60 text-slate-100 focus-visible:ring-vortex-cyan" placeholder="Your name" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="fb-email" className="font-mono text-[10px] tracking-[0.25em] text-slate-400 uppercase">
                      Email{quickMode ? " (optional — for a reply)" : ""}
                    </Label>
                    <Input id="fb-email" data-testid="feedback-email-input" required={!quickMode} type="email" value={form.email} onChange={set("email")}
                      className="border-white/10 bg-vortex-ink/60 text-slate-100 focus-visible:ring-vortex-cyan" placeholder="you@company.com" />
                  </div>
                </div>
                <div className="mt-6 space-y-2">
                  <Label className="font-mono text-[10px] tracking-[0.25em] text-slate-400 uppercase">Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="feedback-category-select" className="border-white/10 bg-vortex-ink/60 text-slate-100 focus:ring-vortex-cyan">
                      <SelectValue placeholder="What is this about?" />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-vortex-surface text-slate-100">
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c} value={c} data-testid={`feedback-category-option-${c.toLowerCase().replace(/\s+/g, "-")}`}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="mt-6 space-y-2">
                  <Label htmlFor="fb-message" className="font-mono text-[10px] tracking-[0.25em] text-slate-400 uppercase">Message</Label>
                  <Textarea id="fb-message" maxLength={5000} data-testid="feedback-message-input" required rows={6} value={form.message} onChange={set("message")}
                    className="border-white/10 bg-vortex-ink/60 text-slate-100 focus-visible:ring-vortex-cyan"
                    placeholder="More sparkling water flavors? A machine issue? Tell us everything..." />
                </div>
                <input type="text" name="company" value={form.company} onChange={set("company")} tabIndex={-1} autoComplete="off" aria-hidden="true" data-testid="feedback-honeypot" style={{ position: "absolute", left: "-9999px", opacity: 0 }} />
                <HumanCheck captcha={captcha.captcha} answer={captcha.answer} setAnswer={captcha.setAnswer} onRefresh={captcha.load} />
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="feedback-submit-button"
                  className="group mt-10 flex w-full items-center justify-center gap-3 rounded-full bg-vortex-cyan px-10 py-5 font-mono text-xs tracking-[0.2em] uppercase text-vortex-ink transition-all duration-300 hover:shadow-[0_0_40px_rgba(92,179,226,0.5)] disabled:opacity-50 sm:w-auto"
                >
                  {sending ? "Sending..." : "Send feedback"}
                  <Send size={15} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
                </button>
                <p className="mt-4 font-mono text-[10px] tracking-[0.2em] text-slate-600 uppercase">
                  {quickMode
                    ? "No email needed — your message goes straight to our team."
                    : "You will receive a confirmation email right away."}
                </p>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Feedback;
